class GalleryModal {
  constructor(el) {
    this.roots = document.querySelectorAll('.' + el);
    if (!this.roots.length) return;

    this.durationMs = 300;
    this.reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    this.activeDialog = null;
    this.closing = new WeakSet();
    this.state = new WeakMap();

    this.roots.forEach((root) => this.bindRoot(root));
  }

  bindRoot(root) {
    const dialog = root.querySelector('[data-gallery-modal]');
    const img = root.querySelector('[data-gallery-modal-img]');
    const caption = root.querySelector('[data-gallery-modal-caption]');
    const counter = root.querySelector('[data-gallery-counter]');
    if (!dialog || !img) return;

    this.state.set(dialog, {
      root,
      img,
      caption,
      counter,
      items: [],
      index: 0,
      targetIndex: 0,
      fading: false,
      fadeTimer: 0,
      onImgReady: null,
      scrollLocked: false,
      swipe: {
        active: false,
        pointerId: null,
        startX: 0,
        startY: 0,
        locked: null,
        suppressClick: false,
      },
    });

    img.addEventListener('dragstart', (event) => {
      event.preventDefault();
    });

    this.bindSwipe(dialog, root.querySelector('[data-gallery-swipe]') || img);
    this.bindOpenGuard(root, dialog);

    root.addEventListener('click', (event) => {
      const openTrigger = event.target.closest('[data-gallery-open]');
      if (openTrigger && root.contains(openTrigger)) {
        // スワイプ後の合成 click や Swiper の preventClicks を無視して開かない
        if (event.defaultPrevented || this.consumeOpenSuppressed(dialog)) {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        this.open(dialog, openTrigger);
        return;
      }

      const closeTrigger = event.target.closest('[data-gallery-close]');
      if (closeTrigger && root.contains(closeTrigger)) {
        event.preventDefault();
        this.close(dialog);
        return;
      }

      const prevTrigger = event.target.closest('[data-gallery-prev]');
      if (prevTrigger && root.contains(prevTrigger)) {
        event.preventDefault();
        this.step(dialog, -1);
        return;
      }

      const nextTrigger = event.target.closest('[data-gallery-next]');
      if (nextTrigger && root.contains(nextTrigger)) {
        event.preventDefault();
        this.step(dialog, 1);
      }
    });

    dialog.addEventListener('click', (event) => {
      const state = this.state.get(dialog);
      if (state && state.swipe.suppressClick) {
        state.swipe.suppressClick = false;
        event.preventDefault();
        return;
      }

      if (
        event.target.closest(
          '[data-gallery-modal-img], [data-gallery-modal-caption], [data-gallery-prev], [data-gallery-next], [data-gallery-close], [data-gallery-counter]'
        )
      ) {
        return;
      }
      this.close(dialog);
    });

    // Esc の即閉じを止め、フェードアウトしてから閉じる
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close(dialog);
    });

    dialog.addEventListener('keydown', (event) => {
      if (!dialog.open && !dialog.hasAttribute('open')) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(dialog, -1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(dialog, 1);
      }
    });

    dialog.addEventListener('close', () => {
      dialog.classList.remove('is-open', 'is-closing');
      this.closing.delete(dialog);
      if (this.activeDialog === dialog) {
        this.activeDialog = null;
      }
      const state = this.state.get(dialog);
      if (state) {
        this.unlockScroll(state);
        this.resetFade(state);
        state.img.classList.remove('is-switching');
        state.img.removeAttribute('src');
        state.img.alt = '';
        this.applyCaption(state, '');
        state.items = [];
        state.index = 0;
        state.targetIndex = 0;
        if (state.counter) {
          state.counter.textContent = '1 / 1';
        }
        this.resetSwipe(state);
      }
    });
  }

  // スライド操作中の指離れでモーダルが開かないよう、移動量で開く操作を抑止する
  bindOpenGuard(root, dialog) {
    const state = this.state.get(dialog);
    if (!state) return;

    state.openGuard = {
      pointerId: null,
      startX: 0,
      startY: 0,
      suppress: false,
    };

    const moveThreshold = 8;

    root.addEventListener('pointerdown', (event) => {
      const openTrigger = event.target.closest('[data-gallery-open]');
      if (!openTrigger || !root.contains(openTrigger)) return;
      if (event.isPrimary === false) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      state.openGuard.pointerId = event.pointerId;
      state.openGuard.startX = event.clientX;
      state.openGuard.startY = event.clientY;
      state.openGuard.suppress = false;
    });

    root.addEventListener('pointermove', (event) => {
      const guard = state.openGuard;
      if (!guard || guard.pointerId === null) return;
      if (event.pointerId !== guard.pointerId || guard.suppress) return;

      const dx = event.clientX - guard.startX;
      const dy = event.clientY - guard.startY;
      if (Math.abs(dx) > moveThreshold || Math.abs(dy) > moveThreshold) {
        guard.suppress = true;
      }
    });

    const endPointer = (event) => {
      const guard = state.openGuard;
      if (!guard || guard.pointerId === null) return;
      if (event.pointerId !== guard.pointerId) return;
      guard.pointerId = null;
    };

    root.addEventListener('pointerup', endPointer);
    root.addEventListener('pointercancel', (event) => {
      const guard = state.openGuard;
      if (!guard || guard.pointerId === null) return;
      if (event.pointerId !== guard.pointerId) return;
      guard.pointerId = null;
      guard.suppress = false;
    });
  }

  consumeOpenSuppressed(dialog) {
    const state = this.state.get(dialog);
    if (!state || !state.openGuard || !state.openGuard.suppress) return false;
    state.openGuard.suppress = false;
    return true;
  }

  lockScroll(state) {
    if (!state || state.scrollLocked) return;
    if (typeof window.lockBodyScroll !== 'function') return;
    window.lockBodyScroll();
    state.scrollLocked = true;
  }

  unlockScroll(state) {
    if (!state || !state.scrollLocked) return;
    if (typeof window.unlockBodyScroll === 'function') {
      window.unlockBodyScroll();
    }
    state.scrollLocked = false;
  }

  bindSwipe(dialog, target) {
    if (!target) return;

    const isControl = (event) =>
      !!event.target.closest(
        '[data-gallery-prev], [data-gallery-next], [data-gallery-close]'
      );

    target.addEventListener('pointerdown', (event) => {
      if (isControl(event)) return;
      this.onSwipeStart(dialog, event);
    });

    target.addEventListener(
      'pointermove',
      (event) => {
        this.onSwipeMove(dialog, target, event);
      },
      { passive: false }
    );

    target.addEventListener('pointerup', (event) => {
      this.onSwipeEnd(dialog, event);
    });

    target.addEventListener('pointercancel', () => {
      const state = this.state.get(dialog);
      if (state) this.resetSwipe(state);
    });
  }

  onSwipeStart(dialog, event) {
    const state = this.state.get(dialog);
    if (!state || this.closing.has(dialog)) return;
    if (!dialog.open && !dialog.hasAttribute('open')) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.isPrimary === false) return;

    if (event.pointerType === 'mouse') {
      event.preventDefault();
    }

    state.swipe.active = true;
    state.swipe.pointerId = event.pointerId;
    state.swipe.startX = event.clientX;
    state.swipe.startY = event.clientY;
    state.swipe.locked = null;
    state.swipe.suppressClick = false;
  }

  onSwipeMove(dialog, target, event) {
    const state = this.state.get(dialog);
    if (!state || !state.swipe.active) return;
    if (event.pointerId !== state.swipe.pointerId) return;

    const dx = event.clientX - state.swipe.startX;
    const dy = event.clientY - state.swipe.startY;
    const lockThreshold = 8;

    if (
      state.swipe.locked === null &&
      (Math.abs(dx) > lockThreshold || Math.abs(dy) > lockThreshold)
    ) {
      state.swipe.locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (state.swipe.locked === 'x') {
        dialog.classList.add('is-swiping');
        if (target.setPointerCapture) {
          target.setPointerCapture(event.pointerId);
        }
      }
    }

    if (state.swipe.locked === 'x') {
      event.preventDefault();
    }
  }

  onSwipeEnd(dialog, event) {
    const state = this.state.get(dialog);
    if (!state || !state.swipe.active) return;
    if (event.pointerId !== state.swipe.pointerId) return;

    const dx = event.clientX - state.swipe.startX;
    const wasHorizontal = state.swipe.locked === 'x';
    const swipeThreshold = 40;

    this.resetSwipe(state);

    if (!wasHorizontal || Math.abs(dx) < swipeThreshold) return;

    state.swipe.suppressClick = true;
    this.step(dialog, dx < 0 ? 1 : -1);
  }

  resetSwipe(state) {
    if (!state || !state.swipe) return;
    state.swipe.active = false;
    state.swipe.pointerId = null;
    state.swipe.startX = 0;
    state.swipe.startY = 0;
    state.swipe.locked = null;
    const dialog = state.img && state.img.closest('[data-gallery-modal]');
    if (dialog) {
      dialog.classList.remove('is-swiping');
    }
  }

  getItems(root, trigger) {
    const group = trigger.closest('[data-gallery-group]') || root;
    return Array.from(group.querySelectorAll('[data-gallery-open]')).filter(
      (el) =>
        !el.closest('.swiper-slide-duplicate') &&
        !el.closest('[data-job-slider-clone]')
    );
  }

  wrapIndex(index, len) {
    return ((index % len) + len) % len;
  }

  applyItem(state, index) {
    const len = state.items.length;
    state.index = this.wrapIndex(index, len);
    state.targetIndex = state.index;
    const trigger = state.items[state.index];
    const captionText = (trigger.getAttribute('data-full-caption') || '').trim();
    state.img.src = trigger.getAttribute('data-full-src') || '';
    state.img.alt = trigger.getAttribute('data-full-alt') || captionText;
    this.applyCaption(state, captionText);

    if (state.counter) {
      state.counter.textContent = state.index + 1 + ' / ' + len;
    }
  }

  applyCaption(state, text) {
    if (!state.caption) return;

    state.caption.textContent = text;
    if (text) {
      state.caption.removeAttribute('hidden');
    } else {
      state.caption.setAttribute('hidden', '');
    }
  }

  resetFade(state) {
    state.fading = false;
    if (state.fadeTimer) {
      window.clearTimeout(state.fadeTimer);
      state.fadeTimer = 0;
    }
    if (state.onImgReady) {
      state.img.removeEventListener('load', state.onImgReady);
      state.img.removeEventListener('error', state.onImgReady);
      state.onImgReady = null;
    }
  }

  waitOpacity(img, done) {
    if (this.reduceMotion) {
      done();
      return;
    }

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      img.removeEventListener('transitionend', onEnd);
      done();
    };

    const onEnd = (event) => {
      if (event.target !== img || event.propertyName !== 'opacity') return;
      finish();
    };

    img.addEventListener('transitionend', onEnd);
    window.setTimeout(finish, this.durationMs + 50);
  }

  waitImage(state, done) {
    const img = state.img;
    if (this.reduceMotion || (img.complete && img.naturalWidth > 0)) {
      done();
      return;
    }

    const onReady = () => {
      if (state.onImgReady !== onReady) return;
      state.onImgReady = null;
      img.removeEventListener('load', onReady);
      img.removeEventListener('error', onReady);
      done();
    };

    state.onImgReady = onReady;
    img.addEventListener('load', onReady);
    img.addEventListener('error', onReady);
  }

  fadeToItem(dialog, index) {
    const state = this.state.get(dialog);
    if (!state) return;

    const img = state.img;
    const alreadyHidden = img.classList.contains('is-switching');
    state.fading = true;
    state.targetIndex = index;

    const afterOut = () => {
      if (this.closing.has(dialog) || (!dialog.open && !dialog.hasAttribute('open'))) {
        return;
      }

      this.applyItem(state, state.targetIndex);

      const afterIn = () => {
        if (this.closing.has(dialog)) return;
        if (state.targetIndex !== state.index) {
          this.fadeToItem(dialog, state.targetIndex);
          return;
        }
        state.fading = false;
      };

      const reveal = () => {
        if (this.closing.has(dialog)) return;
        this.waitOpacity(img, afterIn);
        img.classList.remove('is-switching');
      };

      this.waitImage(state, () => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(reveal);
        });
      });
    };

    if (alreadyHidden) {
      afterOut();
      return;
    }

    this.waitOpacity(img, afterOut);
    img.classList.add('is-switching');
  }

  showItem(dialog, index, fade) {
    const state = this.state.get(dialog);
    if (!state || !state.items.length) return;

    const next = this.wrapIndex(index, state.items.length);

    if (!fade || this.reduceMotion) {
      this.resetFade(state);
      this.applyItem(state, next);
      state.img.classList.remove('is-switching');
      return;
    }

    if (next === state.index && !state.fading && state.img.getAttribute('src')) {
      return;
    }

    if (state.fading) {
      state.targetIndex = next;
      return;
    }

    this.fadeToItem(dialog, next);
  }

  step(dialog, delta) {
    const state = this.state.get(dialog);
    if (!state || !state.items.length || this.closing.has(dialog)) return;

    const from = state.fading ? state.targetIndex : state.index;
    this.showItem(dialog, from + delta, true);
  }

  open(dialog, trigger) {
    const state = this.state.get(dialog);
    if (!state || this.closing.has(dialog)) return;

    const items = this.getItems(state.root, trigger);
    const src = trigger.getAttribute('data-full-src') || '';
    if (!src || !items.length) return;

    state.items = items;
    let index = items.indexOf(trigger);
    if (index < 0) {
      index = items.findIndex(
        (el) => el.getAttribute('data-full-src') === src
      );
    }
    this.showItem(dialog, index >= 0 ? index : 0, false);

    dialog.classList.remove('is-closing');

    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) {
        dialog.showModal();
        this.lockScroll(state);
      }
    } else if (!dialog.hasAttribute('open')) {
      dialog.setAttribute('open', '');
      this.lockScroll(state);
    }

    this.activeDialog = dialog;

    if (this.reduceMotion) {
      dialog.classList.add('is-open');
      return;
    }

    // 開いた直後に opacity 0 → 1 へ遷移させる
    dialog.classList.remove('is-open');
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        dialog.classList.add('is-open');
      });
    });
  }

  close(dialog) {
    if (!dialog || this.closing.has(dialog)) return;
    if (!dialog.open && !dialog.hasAttribute('open')) return;

    const state = this.state.get(dialog);
    if (state) {
      this.resetFade(state);
    }

    if (this.reduceMotion || !dialog.classList.contains('is-open')) {
      this.finishClose(dialog);
      return;
    }

    this.closing.add(dialog);
    dialog.classList.add('is-closing');
    dialog.classList.remove('is-open');

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      dialog.removeEventListener('transitionend', onEnd);
      window.clearTimeout(fallbackId);
      this.finishClose(dialog);
    };

    const onEnd = (event) => {
      if (event.target !== dialog || event.propertyName !== 'opacity') return;
      finish();
    };

    dialog.addEventListener('transitionend', onEnd);
    const fallbackId = window.setTimeout(finish, this.durationMs + 50);
  }

  finishClose(dialog) {
    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
      return;
    }

    dialog.removeAttribute('open');
    dialog.dispatchEvent(new Event('close'));
  }
}
